import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./footer.css";

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef(null);
  const contentRef = useRef([]);
  const socialRef = useRef([]);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: "LinkedIn", icon: "fa-brands fa-linkedin-in", url: "https://www.linkedin.com/company/yaeinpc/", color: "#0A66C2" },
    { name: "Twitter", icon: "fa-brands fa-x-twitter", url: "https://x.com/yaeinpc", color: "#1DA1F2" },
    { name: "Facebook", icon: "fa-brands fa-facebook-f", url: "https://www.facebook.com/YAEINPC", color: "#1877F2" },
    { name: "Instagram", icon: "fa-brands fa-instagram", url: "https://www.instagram.com/yaeinpc/", color: "#E4405F" },
    // { name: "GitHub", icon: "fa-brands fa-github", url: "https://github.com/inteksolutions", color: "#333" },
  ];

  const products = [
    { name: "IIMS", link: "https://iims.africa" },
    { name: "E-MALI", link: "#products" },
    { name: "YAEI-Connect", link: "https://yaeiacademy.co.za" },
    { name: "AI-DSS", link: "#products" },
    { name: "FlexiHub", link: "https://flexi.africa" },
  ];

  const quickLinks = [
    { name: "Home", link: "#home" },
    { name: "Services", link: "#services" },
    { name: "Projects", link: "#projects" },
    { name: "Team", link: "#team" },
    { name: "Contact", link: "#contact1" },
  ];

  const contactInfo = {
    email: "info@inteksolutions.co.za",
    phone: "063 589 7747",
    address: ["Fourways Golf Park", "32 Roos Street", "Fourways", "2191"],
  };

  useEffect(() => {
    gsap.fromTo(
      contentRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: { trigger: footerRef.current, start: "top 90%", toggleActions: "play none none reverse" },
      }
    );

    gsap.fromTo(
      socialRef.current,
      { scale: 0, rotation: -180 },
      {
        scale: 1,
        rotation: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "back.out(2)",
        scrollTrigger: { trigger: footerRef.current, start: "top 85%", toggleActions: "play none none reverse" },
      }
    );
  }, []);

  // Lock body scroll while modal open
  useEffect(() => {
    document.body.style.overflow = showPrivacy ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showPrivacy]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer
      ref={footerRef}
      className="footer-section"
      style={{
        background: "linear-gradient(135deg, #0F0C1F 0%, #1a1635 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Decoration */}
      <div className="footer-bg-decoration">
        <div className="footer-grid-pattern"></div>
        <div className="footer-gradient-circle footer-gradient-circle-1"></div>
        <div className="footer-gradient-circle footer-gradient-circle-2"></div>
      </div>

      <Container className="position-relative" style={{ zIndex: 2 }}>
        {/* Main Footer Content */}
        <Row className="footer-main py-5">
          {/* Company Info */}
          <Col lg={4} md={6} className="mb-4" ref={(el) => (contentRef.current[0] = el)}>
            <div className="footer-brand mb-4">
              <img
                src={process.env.PUBLIC_URL + "/images/landing/logo.svg"}
                alt="InTek Solutions"
                style={{ height: "100px", width: "100px" }}
              />
              {/* <h5 style={{ color: "#2451a8", fontWeight: "bold", marginTop: "0.5rem", textAlign: "left" }}>
                InTek Solutions
              </h5> */}
            </div>
            <p className="footer-description">
              Leading ICT firm dedicated to meeting the digital and electronic
              needs of businesses. From strategic consulting to software
              development and cybersecurity solutions.
            </p>

            {/* Social Media */}
            <div className="footer-social">
              {socialLinks.map((social, index) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  ref={(el) => (socialRef.current[index] = el)}
                  className="footer-social-icon"
                  style={{ "--icon-color": social.color }}
                  aria-label={social.name}
                >
                  <i className={social.icon}></i>
                </a>
              ))}
            </div>
          </Col>

          {/* Products */}
          <Col lg={2} md={6} className="mb-4" ref={(el) => (contentRef.current[1] = el)}>
            <h6 className="footer-heading">Products</h6>
            <ul className="footer-links">
              {products.map((product) => (
                <li key={product.name}>
                  <a href={product.link}>{product.name}</a>
                </li>
              ))}
            </ul>
          </Col>

          {/* Quick Links */}
          <Col lg={2} md={6} className="mb-4" ref={(el) => (contentRef.current[2] = el)}>
            <h6 className="footer-heading">Quick Links</h6>
            <ul className="footer-links">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.link}>{link.name}</a>
                </li>
              ))}
            </ul>
          </Col>

          {/* Contact Info */}
          <Col lg={4} md={6} className="mb-4" ref={(el) => (contentRef.current[3] = el)}>
            <h6 className="footer-heading">Get In Touch</h6>
            <div className="footer-contact">
              <div className="footer-contact-item">
                <i className="fa-solid fa-envelope"></i>
                <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
              </div>
              <div className="footer-contact-item">
                <i className="fa-solid fa-phone"></i>
                <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a>
              </div>
              <div className="footer-contact-item">
                <i className="fa-solid fa-location-dot"></i>
                <div>
                  {contactInfo.address.map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Divider */}
        <div className="footer-divider"></div>

        {/* Bottom Bar */}
        <div className="footer-bottom py-4">
          <Row className="align-items-center">
            <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
              <p className="footer-copyright">
                © {currentYear} InTek Solutions. All rights reserved.
              </p>
            </Col>
            <Col md={6} className="text-center text-md-end">
              <div className="footer-bottom-links">
                <button
                  type="button"
                  className="footer-legal-link"
                  onClick={() => setShowPrivacy(true)}
                >
                  Privacy Policy
                </button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Back to Top Button */}
      <button onClick={scrollToTop} className="back-to-top" aria-label="Back to top">
        <i className="fa-solid fa-arrow-up"></i>
      </button>

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="policy-modal-overlay" onClick={() => setShowPrivacy(false)}>
          <div
            className="policy-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="policy-title"
          >
            <div className="policy-modal-header">
              <h2 id="policy-title">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacy(false)}
                className="policy-modal-close"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="policy-modal-body">
              <h3>Introduction</h3>
              <p>
                Welcome to InTek Solutions' privacy policy. InTek Solutions is a
                private company wholly owned by the African Entrepreneurship
                Development Corporation (AEDC).
              </p>
              <p>
                InTek Solutions (also referred to as "we", "us" or "our" in this
                policy) respects your privacy and is committed to protecting your
                personal information.
              </p>
              <p>This privacy policy explains:</p>
              <ul>
                <li>How we obtain, use and disclose your personal information, in accordance with the requirements of the Protection of Personal Information Act 4 of 2013 ("POPIA");</li>
                <li>How we protect your personal information; and</li>
                <li>Your privacy rights under POPIA.</li>
              </ul>
              <p>Please also use the Glossary at the end of this privacy policy to understand the meaning of some of the terms used here.</p>

              <h3>Important information and who we are</h3>
              <h4>Purpose of this privacy policy</h4>
              <p>
                This privacy policy aims to give you information on how InTek
                Solutions collects and processes your personal information when
                you personally or on behalf of a juristic entity:
              </p>
              <ul>
                <li>visit this website or any other website that is owned or operated by us;</li>
                <li>enquire about or use any of our services, including consulting engagements, product demos, and training;</li>
                <li>register as a user of any of our products (eBin, E-MALI, YAEI-Connect, AI-DSS, FlexiHub);</li>
                <li>engage with us on prospective contracts or other arrangements with us; and</li>
                <li>conclude a contract with us, for example, where you act as a supplier, vendor, agent, or partner.</li>
              </ul>
              <p>
                It is important that you read this privacy policy together with
                any other privacy policy or document we may provide on specific
                occasions when we are collecting or processing personal
                information about you so that you are fully aware of how and why
                we are using your information. This privacy policy supplements
                other notices and privacy policies and is not intended to
                override them.
              </p>

              <h4>Responsible party</h4>
              <p>
                In respect of the circumstances to which this privacy policy
                applies, InTek Solutions is the responsible party and is
                responsible for your personal information.
              </p>
              <p>
                We have appointed an Information Officer who is responsible for
                overseeing questions in relation to this privacy policy. If you
                have any questions about this privacy policy, including any
                requests to exercise your legal rights, please contact the
                Information Officer using the details set out below.
              </p>

              <h4>Contact details</h4>
              <p>If you have any questions about this privacy policy or our privacy practices, please contact our Information Officer:</p>
              <ul>
                <li><strong>Full name of legal entity:</strong> InTek Solutions (Pty) Ltd, a subsidiary of the African Entrepreneurship Development Corporation (AEDC)</li>
                <li><strong>Email address:</strong> info@inteksolutions.co.za</li>
                <li><strong>Postal address:</strong> Fourways Golf Park, 32 Roos Street, Fourways, 2191</li>
                <li><strong>Telephone number:</strong> 063 589 7747</li>
              </ul>
              <p>
                You have the right to make a complaint at any time to the
                Information Regulator, the South African regulatory authority
                for personal information protection issues
                (<a href="https://inforegulator.org.za/" target="_blank" rel="noopener noreferrer">inforegulator.org.za</a>).
                We would however appreciate the opportunity to deal with your
                concerns before you approach the Information Regulator, so
                please contact us in the first instance.
              </p>

              <h4>Changes to the privacy policy and your duty to inform us of changes</h4>
              <p>
                We keep our privacy policy under regular review. This version
                was last updated on 26 August 2026. Historic versions can be
                obtained by contacting us.
              </p>
              <p>
                It is important that the personal information we hold about you
                is accurate and current. Please keep us informed if your
                personal information changes during your relationship with us.
              </p>

              <h4>Third-party links</h4>
              <p>
                This website (or any other website that is owned or operated by
                us or by AEDC) may include links to third-party websites,
                plug-ins and applications. Clicking on those links or enabling
                those connections may allow third parties to collect or share
                information about you. We do not control these third-party
                websites and are not responsible for their privacy statements.
                We encourage you to read the privacy policy of every website you
                visit.
              </p>

              <h3>The personal information we collect about you</h3>
              <p>
                Personal information means information about a natural or
                juristic person from which that person can be identified. It
                does not include information where the identity has been
                removed (anonymous information).
              </p>
              <p>We may collect, use, store and transfer different kinds of personal information which we have grouped together as follows:</p>
              <ul>
                <li><strong>Identity Information</strong> — first name, last name, username or similar identifier, title, date of birth, gender, company registration number, tax number, and due diligence (know-your-client) information.</li>
                <li><strong>Contact Information</strong> — billing address, delivery address, email address and telephone numbers.</li>
                <li><strong>Financial Information</strong> — bank account and payment card details, where relevant to a product like FlexiHub or a commercial engagement with us.</li>
                <li><strong>Transaction Information</strong> — details about payments or invoicing arrangements that apply to our relationship with you.</li>
                <li><strong>Technical Information</strong> — internet protocol (IP) address, login information, browser type and version, time zone setting and location, operating system and platform, and other technology on the devices you use to access this website or our products.</li>
                <li><strong>Usage Information</strong> — how you use our website and products.</li>
                <li><strong>Marketing and Communications Information</strong> — your preferences in receiving marketing from us and our third parties, and your communication preferences.</li>
              </ul>

              <h4>Special personal information</h4>
              <p>
                If we collect Special Personal Information about you (race or
                ethnicity, religious or philosophical beliefs, sex life, sexual
                orientation, political opinions, trade union membership,
                criminal convictions and offences, health, and genetic and
                biometric information) we will only do so with your consent or
                where otherwise allowed by law.
              </p>

              <h4>Personal information relating to children</h4>
              <p>
                InTek Solutions does not knowingly process any children's
                personal information and our services and communications are
                not addressed to anybody under 18 years of age. If we become
                aware that we have collected any children's personal
                information without a lawful basis, we will take steps to
                remove such information from our database.
              </p>

              <h3>How is your personal information collected?</h3>
              <p>We use different methods to collect data from and about you including:</p>
              <ul>
                <li><strong>Direct interactions.</strong> You may give us your Identity, Contact, Financial and other information by interacting directly with us — filling in forms on our website, requesting a product demo, subscribing to updates, or corresponding with us by email, phone or otherwise.</li>
                <li><strong>Automated technologies.</strong> As you interact with our website and products, we may automatically collect Technical Information about your equipment, browsing actions and patterns using cookies and similar technologies.</li>
                <li><strong>Third parties or publicly available sources.</strong> We may receive personal information about you from analytics providers, payment processors, and publicly available sources.</li>
              </ul>

              <h3>How we use your personal information</h3>
              <p>We will only use your personal information as the law allows. Most commonly, we will use your personal information in the following circumstances:</p>
              <ul>
                <li>Where we need to perform a contract we are about to enter into or have entered into with you;</li>
                <li>Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests;</li>
                <li>Where we need to comply with a legal obligation;</li>
                <li>Where we have obtained your consent to process your personal information for the specific purposes described to you.</li>
              </ul>

              <h4>Marketing</h4>
              <p>
                We may use your Identity, Contact, Technical, Usage and
                Marketing Information to form a view on what we think you may
                want or need. You will receive marketing communications from us
                if you have requested information from us or engaged with our
                products, and you have not opted out. You can ask us to stop
                sending you marketing messages at any time by following the
                opt-out links on any marketing message or by contacting us.
              </p>

              <h4>Cookies</h4>
              <p>
                You can set your browser to refuse all or some browser cookies,
                or to alert you when websites set or access cookies. If you
                disable or refuse cookies, please note that some parts of this
                website may become inaccessible or not function properly.
              </p>

              <h3>Disclosures of your personal information</h3>
              <p>We may share your personal information with:</p>
              <ul>
                <li>Our employees and affiliates within the InTek Solutions and AEDC group.</li>
                <li>External third parties in relation to the performance of any contract or business with you, or to whom we subcontract to provide our services (like software vendors and IT service providers).</li>
                <li>Legal advisers, insurers and auditors where necessary.</li>
                <li>Law enforcement bodies, regulators and government authorities, where necessary to comply with legal or regulatory obligations.</li>
                <li>Any other person with your express consent.</li>
              </ul>
              <p>We require all third parties to respect the security of your personal information and to treat it in accordance with the law.</p>

              <h3>Cross-border transfers of personal information</h3>
              <p>
                We do not transfer your personal information outside the
                Republic of South Africa. Where any of our systems or providers
                are located outside South Africa, we will only transfer your
                information where a lawful basis for doing so exists under
                POPIA.
              </p>

              <h3>Security</h3>
              <p>
                We have put in place appropriate security measures to prevent
                your personal information from being lost, damaged, or accessed
                in an unauthorised way. We limit access to your personal
                information to those employees, agents, contractors and other
                third parties who have a business need to know. We have put in
                place procedures to deal with any suspected data breach and
                will notify you and any applicable regulator of a breach where
                we are legally required to do so.
              </p>

              <h3>Our retention of your personal information</h3>
              <p>
                We will only retain your personal information for as long as
                reasonably necessary to fulfil the purposes we collected it
                for, unless retention is required or authorised by law, we
                reasonably require the information for lawful purposes relating
                to one of our functions or activities, retention is required by
                a contract entered into between us, or you consent to such
                retention.
              </p>

              <h3>Your legal rights</h3>
              <p>Under POPIA, you have the following rights in relation to your personal information:</p>
              <ul>
                <li><strong>Notification</strong> that your personal information is being collected, and where it was collected from.</li>
                <li><strong>Notification of security compromises</strong> where your information has been accessed or acquired by an unauthorised person.</li>
                <li><strong>Request access</strong> to your personal information and a copy or description of that record.</li>
                <li><strong>Request correction</strong> or deletion of your personal information where it is inaccurate, irrelevant, excessive, out of date, incomplete, misleading or obtained unlawfully.</li>
                <li><strong>Request destruction or deletion</strong> of your personal information where there is no good reason for us continuing to process it.</li>
                <li><strong>Object to processing</strong> of your personal information for direct marketing purposes or where we are relying on a legitimate interest.</li>
                <li><strong>Request restriction of processing</strong> in certain scenarios.</li>
                <li><strong>Withdraw consent</strong> at any time where we are relying on your consent to process your personal information.</li>
                <li><strong>Submit a complaint</strong> to the Information Regulator.</li>
                <li><strong>Institute civil proceedings</strong> regarding the alleged interference with the protection of your personal information.</li>
              </ul>
              <p>If you wish to exercise any of these rights, please contact us using the details above.</p>

              <h3>Glossary</h3>
              <p><strong>Information Regulator</strong> means South Africa's Information Regulator appointed in terms of section 39 of POPIA.</p>
              <p><strong>Personal information</strong> means all information relating to individuals that is processed in terms of this privacy policy, and where applicable, information relating to an identifiable, existing juristic person.</p>
              <p><strong>POPIA</strong> means the Protection of Personal Information Act, 2013 and its regulations.</p>
              <p><strong>Process</strong> means any operation or activity concerning personal information, including collection, receipt, recording, organisation, storage, updating, retrieval, use, dissemination, merging, restriction, erasure or destruction.</p>
              <p><strong>Responsible party</strong> means a public or private body or any other person which, alone or in conjunction with others, determines the purpose of and means for processing personal information. For the purposes of this privacy policy that is InTek Solutions.</p>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;